import type React from 'react';
import { useEffect } from 'react';
import { Box, Icon, IconButton, Paragraph } from '@zillow/constellation-10';
import { IconDownloadFilled, IconExternalFilled } from '@zillow/constellation-icons';
import { format } from 'date-fns';
import AttachmentIcon from '../AttachmentIcon';
import hasMissingAttachmentContent from '../../../utils/hasMissingAttachmentContent';
import TooltipWrapper from '../../TooltipWrapper';
import { formatBytes } from '../../../utils/formatBytes';
import { ATTACHMENT_STATUS, FILE_TYPES } from '../../../constants';
import ExpiredAttachmentCard from '../ExpiredAttachmentCard';
import { getAttachmentCardStyles } from '../styles';
import type { Attachment } from '../../../types/attachment';

const { IMAGE, VIDEO } = FILE_TYPES;
const { IMPERSONATION_DETECTED } = ATTACHMENT_STATUS;

type AttachmentCardProps = {
  attachment: Attachment;
  variant?: 'sidebar' | 'message';
  isMessageOwner?: boolean;
  showDateAdded?: boolean;
  previewEventId?: string;
  downloadEventId?: string;
  onBeforeAction?: (proceed: () => void) => void;
  onEventTracking: (isImageFileType: boolean) => void;
  onLogError: () => void;
};

export default function AttachmentCard({
  attachment,
  variant = 'sidebar',
  isMessageOwner = false,
  showDateAdded,
  previewEventId,
  downloadEventId,
  onBeforeAction,
  onEventTracking,
  onLogError,
}: AttachmentCardProps) {
  const {
    attachmentAddedMs,
    attachmentMetadataStatus,
    downloadUrl,
    fileName = '',
    fileType = '',
    fileSizeBytes,
    isAttachmentExpired,
    previewUrl,
  } = attachment;

  const shouldShowDateAdded = showDateAdded ?? variant === 'sidebar';
  const hasMissingContent = hasMissingAttachmentContent(attachmentMetadataStatus, isAttachmentExpired);
  const fileSizeFormatted = fileSizeBytes ? formatBytes(fileSizeBytes) : '';
  const dateAdded = attachmentAddedMs ? format(new Date(Number(attachmentAddedMs)), 'MMM dd') : '';
  const href = downloadUrl || previewUrl;
  const isImageFileType = fileType?.includes(IMAGE) ?? false;
  const isVideoFileType = fileType?.includes(VIDEO) ?? false;
  const isImpersonator = downloadUrl === IMPERSONATION_DETECTED || previewUrl === IMPERSONATION_DETECTED;
  const isDisabled = isAttachmentExpired || isImpersonator || hasMissingContent || href === '';
  const isZDocPreviewUrl = previewUrl?.includes('zillowdocs') && previewUrl?.includes('/image/preview');

  // zDocs API is capable of resizing images with height and width params
  // as opposed to images generated on the FE during upload
  const imageSrc = isZDocPreviewUrl ? `${previewUrl}&height=40&width=40` : previewUrl || undefined;

  useEffect(() => {
    if (hasMissingContent) {
      onLogError();
    }
  }, [hasMissingContent, onLogError]);

  if (fileName?.includes('unknown')) {
    return null;
  }

  if (isAttachmentExpired) {
    return (
      <ExpiredAttachmentCard
        fileName={fileName || ''}
        isImageFileType={isImageFileType}
        isVideoFileType={isVideoFileType}
        variant={variant}
        isMessageOwner={isMessageOwner}
      />
    );
  }

  const cardStyles = getAttachmentCardStyles(variant, isMessageOwner);

  return (
    <Box css={cardStyles}>
      <AttachmentIcon
        isImageFileType={isImageFileType}
        isVideoFileType={isVideoFileType}
        imageSrc={imageSrc}
        isDisabled={isDisabled}
      />

      {fileName ? (
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: 'default',
            paddingRight: 'tighter',
            minWidth: 0,
          }}
        >
          <Paragraph textStyle="body-sm" css={{ truncate: true, maxWidth: '100%' }}>
            {fileName}
          </Paragraph>
          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle' }}>
            {fileSizeFormatted}
            {shouldShowDateAdded && dateAdded && <>&nbsp;&bull; Added {dateAdded}</>}
          </Paragraph>
        </Box>
      ) : (
        <Paragraph textStyle="body-xs" css={{ paddingLeft: 'tighter' }}>
          We’re unable to display this file right now
        </Paragraph>
      )}

      <TooltipWrapper tooltipPlacement="top-start" wrapInTooltip={isImpersonator}>
        <IconButton
          asChild
          title={isImageFileType ? 'View image' : 'Download file'}
          data-eventid={isImageFileType ? previewEventId : downloadEventId}
          disabled={isDisabled}
          emphasis="bare"
          size="md"
          tone="brand"
          onClick={(e: React.MouseEvent) => {
            if (onBeforeAction) {
              e.preventDefault();
              onBeforeAction(() => {
                if (isImageFileType) {
                  window.open(previewUrl || '', '_blank', 'noopener,noreferrer');
                } else {
                  const tempLink = document.createElement('a');
                  tempLink.href = downloadUrl || '';
                  tempLink.download = fileName || '';
                  document.body.appendChild(tempLink);
                  tempLink.click();
                  document.body.removeChild(tempLink);
                }
              });
            }
            onEventTracking(isImageFileType);
          }}
          css={{
            marginLeft: 'auto',
            minWidth: '44px',
          }}
        >
          {isImageFileType ? (
            <a href={previewUrl || ''} target="_blank" rel="noreferrer">
              <Icon>
                <IconExternalFilled />
              </Icon>
            </a>
          ) : (
            <a href={downloadUrl || ''} download={fileName}>
              <Icon>
                <IconDownloadFilled />
              </Icon>
            </a>
          )}
        </IconButton>
      </TooltipWrapper>
    </Box>
  );
}
